import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const HomePage = () => {
  const credibilityRef = useScrollAnimation();
  const whoWeAreRef = useScrollAnimation();
  const approachRef = useScrollAnimation();
  const portfolioRef = useScrollAnimation();
  const philosophyRef = useScrollAnimation();
  const communityRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8a9df577b9e3e1284c8e7c912026bb42.jpg"
            alt="Multifamily Property"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F2A44]/70 via-[#1F2A44]/50 to-[#1F2A44]/30"></div>
        </div>

        <div className="relative max-w-[1440px] mx-auto px-20 w-full">
          <div className="max-w-[600px]">
            <div className="w-16 h-0.5 bg-[#B89C4B] mb-8"></div>
            <h1
              className="text-[68px] leading-[1.2] text-white mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Disciplined Multifamily Investing
            </h1>
            <p
              className="text-[19px] text-white/90 mb-12 leading-relaxed"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
            >
              Value-add acquisitions across Midwest markets, built on institutional discipline and community-minded ownership
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/strategy"
                className="px-10 py-3.5 border-2 border-white text-white text-[15px] font-medium rounded transition-all duration-300 hover:bg-white hover:text-[#1F2A44] whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our Strategy
              </Link>
              <Link
                to="/contact"
                className="px-10 py-3.5 text-white text-[15px] font-medium transition-all duration-300 hover:underline whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Bar */}
      <section className="bg-[#F5F5F3] border-t border-b border-[#D9D9D6]">
        <div className="max-w-[1440px] mx-auto px-20 py-8">
          <div ref={credibilityRef.ref} className={`grid grid-cols-1 md:grid-cols-4 gap-8 scroll-animate ${credibilityRef.isVisible ? 'visible' : ''}`}>
            {[
              { label: '10–50 Unit Focus', value: 'Strategic Scale' },
              { label: 'Midwest Markets', value: 'Regional Expertise' },
              { label: 'Value-Add Strategy', value: 'Proven Approach' },
              { label: 'Institutional Discipline', value: 'Conservative Underwriting' },
            ].map((item, index) => (
              <div
                key={index}
                className={`text-center md:border-r md:last:border-r-0 border-[#D9D9D6] scroll-animate-delay-${index + 1}`}
              >
                <p
                  className="text-[13px] text-[#1F2A44] font-semibold uppercase tracking-wider mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.label}
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E]"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={whoWeAreRef.ref} className={`slide-in-left ${whoWeAreRef.isVisible ? 'visible' : ''}`}>
              <p
                className="text-[12px] text-[#B89C4B] font-semibold uppercase tracking-wider mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                About Martell Investment Capital
              </p>
              <h2
                className="text-[42px] text-[#1F2A44] leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Building Value Through Disciplined Ownership
              </h2>
              <div className="space-y-6">
                <p
                  className="text-[17px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Martell Investment Capital is a Midwest-based real estate investment firm specializing in multifamily properties. We focus on acquiring and improving assets where strategic renovations and operational excellence create lasting value.
                </p>
                <p
                  className="text-[17px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our approach combines conservative underwriting with hands-on asset management. We believe in improving housing quality while generating sustainable returns for our capital partners.
                </p>
                <p
                  className="text-[17px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Every property we acquire is evaluated through a disciplined framework that prioritizes capital preservation, operational efficiency, and community impact.
                </p>
              </div>
            </div>
            <div className={`slide-in-right ${whoWeAreRef.isVisible ? 'visible' : ''}`}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20multifamily%20apartment%20building%20with%20modern%20architecture%20clean%20landscaping%20and%20well-maintained%20exterior%20daytime%20architectural%20photography%20showing%20quality%20construction%20and%20attention%20to%20detail%20real%20estate%20investment%20property&width=800&height=900&seq=about001&orientation=portrait"
                alt="Property Excellence"
                className="w-full h-[600px] object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="bg-[#F5F5F3] py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={approachRef.ref} className={`text-center mb-16 scroll-animate ${approachRef.isVisible ? 'visible' : ''}`}>
            <p
              className="text-[12px] text-[#B89C4B] font-semibold uppercase tracking-wider mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Our Process
            </p>
            <h2
              className="text-[48px] text-[#1F2A44] leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Four-Phase Value Creation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-search-line',
                title: 'Acquire',
                description: 'Conservative underwriting focused on properties with clear value-add potential in stable Midwest markets.',
              },
              {
                icon: 'ri-tools-line',
                title: 'Improve',
                description: 'Strategic capital improvements that enhance property quality, resident satisfaction, and market positioning.',
              },
              {
                icon: 'ri-settings-3-line',
                title: 'Operate',
                description: 'Structured property management systems that optimize operations, reduce expenses, and improve tenant retention.',
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Optimize',
                description: 'Disciplined asset management driving NOI growth and positioning properties for long-term value realization.',
              },
            ].map((phase, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer scroll-animate scroll-animate-delay-${index + 1} ${approachRef.isVisible ? 'visible' : ''}`}
              >
                <div className="w-16 h-16 rounded-full border-2 border-[#B89C4B] bg-[#1F2A44] flex items-center justify-center mb-6">
                  <i className={`${phase.icon} text-2xl text-white`}></i>
                </div>
                <h3
                  className="text-[22px] text-[#1F2A44] mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
                >
                  {phase.title}
                </h3>
                <p
                  className="text-[15px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {phase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={portfolioRef.ref} className={`flex justify-between items-end mb-14 scroll-animate ${portfolioRef.isVisible ? 'visible' : ''}`}>
            <h2
              className="text-[44px] text-[#1F2A44]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Select Properties
            </h2>
            <Link
              to="/portfolio"
              className="text-[15px] text-[#B89C4B] font-medium hover:underline cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              View Full Portfolio →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Riverside Commons',
                location: 'Indianapolis, IN',
                units: '42 Units',
                description: 'Value-add multifamily property with strong fundamentals and renovation upside in growing submarket.',
                image: 'https://readdy.ai/api/search-image?query=modern%20multifamily%20apartment%20complex%20with%20brick%20exterior%20professional%20landscaping%20clean%20architecture%20daytime%20photography%20real%20estate%20investment%20property%20midwest%20style&width=600&height=400&seq=prop001&orientation=landscape',
              },
              {
                name: 'Oakwood Terrace',
                location: 'Columbus, OH',
                units: '36 Units',
                description: 'Well-located asset with opportunity for interior upgrades and operational improvements to drive NOI growth.',
                image: 'https://readdy.ai/api/search-image?query=quality%20apartment%20building%20with%20modern%20facade%20well-maintained%20grounds%20professional%20property%20management%20architectural%20photography%20real%20estate%20investment%20midwest%20market&width=600&height=400&seq=prop002&orientation=landscape',
              },
              {
                name: 'Heritage Park',
                location: 'Grand Rapids, MI',
                units: '48 Units',
                description: 'Stabilized property in high-demand area with potential for rent optimization and amenity enhancements.',
                image: 'https://readdy.ai/api/search-image?query=established%20multifamily%20residential%20property%20with%20classic%20architecture%20mature%20landscaping%20quality%20construction%20professional%20real%20estate%20photography%20investment%20grade%20asset&width=600&height=400&seq=prop003&orientation=landscape',
              },
            ].map((property, index) => (
              <div
                key={index}
                className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group scroll-animate scroll-animate-delay-${index + 1} ${portfolioRef.isVisible ? 'visible' : ''}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.name}
                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-9">
                  <h3
                    className="text-[24px] text-[#1F2A44] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
                  >
                    {property.name}
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] mb-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className="ri-map-pin-line text-[#B89C4B] mr-2"></i>
                    {property.location} <span className="text-[#B89C4B] mx-2">•</span> {property.units}
                  </p>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed mb-6"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {property.description}
                  </p>
                  <div className="w-10 h-0.5 bg-[#B89C4B]"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Philosophy Section */}
      <section className="bg-[#1F2A44] py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div ref={philosophyRef.ref} className={`slide-in-left ${philosophyRef.isVisible ? 'visible' : ''}`}>
              <h2
                className="text-[46px] text-white leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Investment Philosophy
              </h2>
              <div className="space-y-6">
                <p
                  className="text-[17px] text-white/85 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our investment approach is grounded in conservative underwriting, disciplined execution, and long-term value creation. We focus on properties where operational improvements and strategic capital deployment can drive sustainable returns.
                </p>
                <p
                  className="text-[17px] text-white/85 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  We prioritize capital preservation and risk-adjusted returns over aggressive growth. Every acquisition is evaluated through multiple stress scenarios to ensure resilience across market cycles.
                </p>
                <p
                  className="text-[17px] text-white/85 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Transparency, alignment, and structured reporting are core to our investor relationships. We believe in building long-term partnerships based on trust and consistent performance.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              {[
                {
                  title: 'Capital Preservation',
                  description: 'Conservative leverage and disciplined underwriting protect investor capital across market cycles.',
                },
                {
                  title: 'Operational Excellence',
                  description: 'Structured property management systems drive efficiency, tenant satisfaction, and NOI growth.',
                },
                {
                  title: 'Strategic Positioning',
                  description: 'Focus on Midwest markets with strong fundamentals, employment growth, and housing demand.',
                },
                {
                  title: 'Long-Term Value',
                  description: 'Patient capital approach focused on sustainable value creation rather than short-term speculation.',
                },
              ].map((principle, index) => (
                <div
                  key={index}
                  className={`bg-white/8 border border-white/15 p-7 rounded-lg scroll-animate scroll-animate-delay-${index + 1} ${philosophyRef.isVisible ? 'visible' : ''}`}
                >
                  <h3
                    className="text-[18px] text-white font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {principle.title}
                  </h3>
                  <p
                    className="text-[15px] text-white/80 leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
                  >
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Community Commitment Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={communityRef.ref} className={`text-center mb-12 max-w-[900px] mx-auto scroll-animate ${communityRef.isVisible ? 'visible' : ''}`}>
            <p
              className="text-[12px] text-[#B89C4B] font-semibold uppercase tracking-wider mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Community Impact
            </p>
            <h2
              className="text-[44px] text-[#1F2A44] leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Responsible Ownership, Lasting Value
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                We believe quality housing strengthens communities. Our commitment extends beyond financial returns to include improving living standards, safety, and neighborhood quality.
              </p>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Every property we acquire undergoes thoughtful improvements that enhance resident experience while contributing to the long-term vitality of the surrounding area.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {[
              {
                icon: 'ri-home-smile-line',
                title: 'Housing Quality',
                description: 'Upgrading units and common areas to improve safety, comfort, and livability for residents.',
              },
              {
                icon: 'ri-community-line',
                title: 'Neighborhood Impact',
                description: 'Enhancing curb appeal and property maintenance to contribute positively to local communities.',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Long-Term Stewardship',
                description: 'Maintaining properties to high standards that preserve value and support stable housing.',
              },
            ].map((item, index) => (
              <div key={index} className={`text-center scroll-animate scroll-animate-delay-${index + 1} ${communityRef.isVisible ? 'visible' : ''}`}>
                <div className="w-12 h-12 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                  <i className={`${item.icon} text-2xl text-[#B89C4B]`}></i>
                </div>
                <h3
                  className="text-[17px] text-[#1F2A44] font-semibold mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-[14px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative h-60">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20apartment%20building%20common%20area%20with%20professional%20interior%20design%20clean%20contemporary%20lobby%20or%20courtyard%20space%20architectural%20photography%20showing%20quality%20amenities%20and%20attention%20to%20detail&width=1920&height=600&seq=cta001&orientation=landscape"
            alt="Partner With Us"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1F2A44]/75"></div>
        </div>

        <div ref={ctaRef.ref} className={`relative h-full flex flex-col items-center justify-center text-center px-6 scale-in-animate ${ctaRef.isVisible ? 'visible' : ''}`}>
          <h2
            className="text-[38px] text-white mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Partner With Us
          </h2>
          <p
            className="text-[16px] text-white/90 mb-8 max-w-[600px]"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Explore investment opportunities with Martell Investment Capital
          </p>
          <Link
            to="/contact"
            className="px-12 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-white hover:shadow-lg whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Schedule a Discussion
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;